import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HStack, SimpleGrid, Text } from "@chakra-ui/react";

import { AcatsTransfer } from "../../../api/acats/acats";
import { useAcatsContraBrokers } from "../../../api/acats/listAcatsContraBrokers";
import { useAccount } from "../../../api/api";
import Card from "../../../components/general/Card";
import UUID from "../../../components/general/UUID";
import {
  fmtCellValue,
  getAccountName,
  toTitleCase,
} from "../../../globals/utils";

interface AcatsTransferDetailsProps {
  data: AcatsTransfer;
}

const ALPACA_DTC_NUMBER = 3021;

const AcatsTransferDetails = ({
  data,
}: AcatsTransferDetailsProps): JSX.Element => {
  const { data: contraBrokers = [] } = useAcatsContraBrokers();
  const { data: account } = useAccount(data.account_id);

  const contraBroker = useMemo(
    () =>
      contraBrokers.find(({ number }) =>
        data?.contra_broker_number.endsWith(number)
      ),
    [data, contraBrokers]
  );

  const navigate = useNavigate();

  const brokerInfo = useMemo(
    () => [
      { label: "DTC Number", value: fmtCellValue(data?.contra_broker_number) },
      {
        label: "Customer Name",
        value: fmtCellValue(data?.account_name),
      },
      {
        label: "Account Type",
        value: fmtCellValue(data?.account_type),
      },
      {
        label: "Account Number",
        value: fmtCellValue(data?.contra_account_number),
      },
    ],
    [data]
  );

  const alpacaInfo = useMemo(
    () => [
      { label: "DTC Number", value: ALPACA_DTC_NUMBER },
      {
        label: "Customer Name",
        value: fmtCellValue(getAccountName(account)),
      },
      {
        label: "Account Type",
        value: fmtCellValue(toTitleCase(account?.account_type ?? "")),
      },
      {
        label: "Account Number",
        value: (
          <UUID
            value={data.account_number}
            onClick={() => navigate(`/accounts/${data.account_id}`)}
          />
        ),
      },
    ],
    [account, data]
  );

  return (
    <SimpleGrid columns={2} gap="1rem" mb="1rem">
      <Card p="1.25rem">
        <Text fontWeight={500} mb="0.5rem">
          Contra Firm: {contraBroker?.name}
        </Text>
        {brokerInfo.map(({ label, value }) => (
          <HStack key={label} gap="2rem">
            <Text fontWeight={400} width="200px">
              {label}
            </Text>
            <Text fontWeight={500}>{value}</Text>
          </HStack>
        ))}
      </Card>
      <Card p="1.25rem">
        <Text fontWeight={500} mb="0.5rem">
          Alpaca Information
        </Text>
        {alpacaInfo.map(({ label, value }) => (
          <HStack key={label} gap="2rem">
            <Text fontWeight={400} width="200px">
              {label}
            </Text>
            <Text fontWeight={500}>{value}</Text>
          </HStack>
        ))}
      </Card>
    </SimpleGrid>
  );
};

export default AcatsTransferDetails;
